import { useState, useEffect } from "react";
import "./Reports.css";
import "./CRM.css"; // Reuse broad styles
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import dataService from "../services/dataService";
import authService from "../services/authService";
import { formatCurrency } from "../utils/formatCurrency";
import { generateReportPDF } from "../utils/reportPDFGenerator";
import { useToast } from "../context/ToastContext";

function ReportScreen() {
  const [activeReport, setActiveReport] = useState("sales");
  const [summary, setSummary] = useState({ totalRevenue: 0, totalExpenses: 0, netProfit: 0, taxPayable: 0 });
  const [salesData, setSalesData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  const handleExport = () => {
    let data = [];

    switch (activeReport) {
      case "sales": data = salesData; break;
      case "inventory": data = inventoryData; break;
      case "profit": data = profitData; break;
      case "expense": data = expenseData; break;
      case "invoiceMonthly": data = invoiceData; break;
      default: return;
    }

    if (!data || data.length === 0) {
      addToast("No data available to export", "error");
      return;
    }

    try {
      generateReportPDF(activeReport === "invoiceMonthly" ? "invoiceMonthly" : activeReport, data, summary);
      addToast(`${activeReport.charAt(0).toUpperCase() + activeReport.slice(1)} report exported as PDF`, "success");
    } catch (err) {
      console.error("PDF Export error:", err);
      addToast("Failed to generate PDF report", "error");
    }
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const [summaryRes, salesRes, inventoryRes, profitRes, expenseRes, invoiceRes] = await Promise.all([
          dataService.getReportSummary(),
          dataService.getSalesReport(),
          dataService.getInventoryReport(),
          dataService.getProfitPerProduct(),
          dataService.getExpenses(),
          dataService.getOrders("Invoice")
        ]);
        setSummary(summaryRes.data.data);
        setSalesData(salesRes.data.data);
        setInventoryData(inventoryRes.data.data);
        setProfitData(profitRes.data.data);
        setExpenseData(expenseRes.data.data);
        setInvoiceData(invoiceRes.data.data);
      } catch (error) {
        console.error("Failed to fetch reports", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Reports & Analytics</h1>
          <p>Comprehensive business insights and reports</p>
        </div>
        {authService.hasPermission('general', 'viewDashboard') && (
          <button className="primary-btn" onClick={handleExport}>
            <span className="btn-icon">📥</span> Export {activeReport.charAt(0).toUpperCase() + activeReport.slice(1)} Report
          </button>
        )}
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info"><span className="stat-label">Total Revenue</span><h2 className="stat-value">{formatCurrency(summary.totalRevenue)}</h2></div>
        </div>
        <div className="stat-card">
          <div className="stat-info"><span className="stat-label">Total Expenses</span><h2 className="stat-value" style={{ color: '#DC2626' }}>{formatCurrency(summary.totalExpenses)}</h2></div>
        </div>
        <div className="stat-card">
          <div className="stat-info"><span className="stat-label">Net Profit</span><h2 className="stat-value" style={{ color: '#00A859' }}>{formatCurrency(summary.netProfit)}</h2></div>
        </div>
        <div className="stat-card">
          <div className="stat-info"><span className="stat-label">Tax Payable</span><h2 className="stat-value" style={{ color: '#EA580C' }}>{formatCurrency(summary.taxPayable)}</h2></div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls"><h2 className="font-bold">Detailed Reports</h2></div>

        <div className="report-tabs-modern">
          <button className={`report-tab-btn ${activeReport === "sales" ? "active" : ""}`} onClick={() => setActiveReport("sales")}>📊 Sales</button>
          <button className={`report-tab-btn ${activeReport === "inventory" ? "active" : ""}`} onClick={() => setActiveReport("inventory")}>📦 Inventory</button>
          <button className={`report-tab-btn ${activeReport === "profit" ? "active" : ""}`} onClick={() => setActiveReport("profit")}>💰 Profit</button>
          <button className={`report-tab-btn ${activeReport === "expense" ? "active" : ""}`} onClick={() => setActiveReport("expense")}>💸 Expenses</button>
          <button className={`report-tab-btn ${activeReport === "invoiceMonthly" ? "active" : ""}`} onClick={() => setActiveReport("invoiceMonthly")}>🧾 Invoices</button>
        </div>

        <div className="report-content-body" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column', width: '100%' }}>
          {loading ? (
            <p style={{ textAlign: 'center', padding: '50px' }}>Loading analytics data...</p>
          ) : (
            <>
              {activeReport === "sales" && (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={salesData}>
                    <XAxis dataKey="month" /><YAxis /><Tooltip /><Legend />
                    <Bar dataKey="sales" fill="#16a34a" name="Sales" />
                    <Bar dataKey="cost" fill="#dc2626" name="Cost" />
                    <Bar dataKey="profit" fill="#2563eb" name="Profit" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {activeReport === "inventory" && (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={inventoryData} layout="vertical">
                    <XAxis type="number" /><YAxis type="category" dataKey="item" width={100} /><Tooltip /><Legend />
                    <Bar dataKey="inStock" fill="#16a34a" name="In Stock" />
                    <Bar dataKey="sold" fill="#2563eb" name="Sold" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {activeReport === "profit" && (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr><th>Product</th><th className="text-right">Sold Qty</th><th className="text-right">Revenue</th><th className="text-right">Cost</th><th className="text-right">Profit</th><th className="text-right">Margin</th></tr>
                    </thead>
                    <tbody>
                      {profitData.map((item, idx) => (
                        <tr key={idx}>
                          <td className="font-bold">{item.product}</td><td className="text-right">{item.totalQuantity}</td>
                          <td className="text-right">{formatCurrency(item.totalRevenue)}</td><td className="text-right">{formatCurrency(item.totalCost)}</td>
                          <td className="text-right" style={{ color: item.profit >= 0 ? '#16a34a' : '#dc2626', fontWeight: 'bold' }}>{formatCurrency(item.profit)}</td>
                          <td className="text-right"><span style={{ background: item.margin >= 20 ? '#dcfce7' : '#fee2e2', color: item.margin >= 20 ? '#166534' : '#991b1b', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{item.margin.toFixed(1)}%</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeReport === "expense" && (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr><th>Date</th><th>Category</th><th>Vendor</th><th className="text-right">Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {expenseData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{new Date(item.date).toLocaleDateString()}</td><td>{item.category}</td><td>{item.vendor}</td>
                          <td className="text-right">{formatCurrency(item.amount)}</td><td><span className={`status-pill ${item.status.toLowerCase()}`}>{item.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeReport === "invoiceMonthly" && (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr><th>Invoice #</th><th>Date</th><th>Customer</th><th className="text-right">Total</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {invoiceData.map((item, idx) => (
                        <tr key={idx}>
                          <td className="font-bold">{item.orderId}</td><td>{new Date(item.date).toLocaleDateString()}</td><td>{item.customer}</td>
                          <td className="text-right">{formatCurrency(item.amount)}</td><td><span className={`status-pill ${item.status.toLowerCase()}`}>{item.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportScreen;